<template>
  <div 
    class="ola-tabs"
    :class="[
      `ola-tabs--${tabPosition}`,
      {
        'ola-tabs--card': type === 'card'
      }
    ]"
  >
    <div class="ola-tabs-header">
      <div 
        v-for="item in panes"
        :key="item.name"
        class="ola-tabs-item"
        :class="{
          'is-active': activeTab === item.name,
          'is-disabled': item.disabled
        }"
        @click="handleTabClick(item)"
      >
        <div v-if="item.useHtml" v-html="item.label"></div>
        <template v-else>{{ item.label }}</template>
      </div>
    </div>
    <div class="ola-tabs-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
/**
 * Tabs 标签页组件
 * @component OlaTabs
 * @description 提供标签页切换功能，支持多种位置和样式
 */

defineOptions({
  name: 'OlaTabs'
})

import { ref, watch, provide, reactive } from 'vue'

// ==================== Props ====================

const props = defineProps({
  /** 当前激活的标签页（v-model） */
  modelValue: {
    type: String,
    default: ''
  },
  /** 标签的类型：默认或卡片 */
  type: {
    type: String,
    default: '',
    validator: (value) => ['', 'card'].includes(value)
  },
  /** 标签的位置：top, right, bottom, left */
  tabPosition: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value)
  }
})

// ==================== Emits ====================

const emit = defineEmits(['update:modelValue', 'tab-click', 'tab-change'])

// ==================== 响应式状态 ====================

/** 标签页列表 */
const panes = ref([])

/** 当前激活的标签页 */
const activeTab = ref(props.modelValue)

// ==================== Watch ====================

// 监听外部 v-model 变化
watch(() => props.modelValue, (val) => {
  activeTab.value = val
})

// 监听内部 activeTab 变化，同步到外部
watch(activeTab, (val) => {
  emit('update:modelValue', val)
  emit('tab-change', val)
})

// ==================== 方法 ====================

/**
 * 添加标签页
 * @param {Object} pane - 标签页配置对象
 */
const addPane = (pane) => {
  panes.value.push(pane)
  // 如果没有设置激活的标签页，或者当前激活的标签页不存在，则激活第一个标签页
  if (!activeTab.value || !panes.value.some(item => item.name === pane.name)) {
    activeTab.value = pane.name
  }
}

/**
 * 移除标签页
 * @param {Object} pane - 标签页配置对象
 */
const removePane = (pane) => {
  const index = panes.value.indexOf(pane)
  if (index > -1) {
    panes.value.splice(index, 1)
  }
  
  // 如果移除的是当前激活的标签页，则激活前一个标签页
  if (pane.name === activeTab.value && panes.value.length) {
    const newIndex = index === 0 ? 0 : index - 1
    activeTab.value = panes.value[newIndex].name
  }
}

/**
 * 处理标签页点击
 * @param {Object} tab - 标签页配置对象
 */
const handleTabClick = (tab) => {
  if (tab.disabled) return
  activeTab.value = tab.name
  emit('tab-click', tab)
}

// ==================== Provide ====================

// 提供给子组件的上下文
provide('tabs', reactive({
  // 使用 getter 确保响应式
  get activeTab() {
    return activeTab.value
  },
  addPane,
  removePane
}))
</script>

<style>
.ola-tabs {
  display: flex;
  flex-direction: column;
}

.ola-tabs-header {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
}

.ola-tabs-item {
  padding: 0 20px;
  height: 40px;
  line-height: 40px;
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  transition: all .3s;
  /* 禁用移动设备上的点击高亮效果 */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.ola-tabs-item:hover {
  color: #333;
}

.ola-tabs-item.is-active {
  color: #333;
  border-bottom: 2px solid #333;
}

.ola-tabs-item.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.ola-tabs-content {
  padding: 15px 0;
}

/* 卡片类型 */
.ola-tabs--card .ola-tabs-header {
  border-bottom: none;
}

.ola-tabs--card .ola-tabs-item {
  border: 1px solid #e4e7ed;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  margin-right: 5px;
  background-color: #f5f7fa;
}

.ola-tabs--card .ola-tabs-item.is-active {
  background-color: #fff;
  border-bottom-color: #fff;
}

/* 位置：右侧 */
.ola-tabs--right {
  flex-direction: row;
}

.ola-tabs--right .ola-tabs-header {
  flex-direction: column;
  border-bottom: none;
  border-left: 1px solid #e4e7ed;
}

.ola-tabs--right .ola-tabs-item {
  border-bottom: none;
}

.ola-tabs--right .ola-tabs-item.is-active {
  border-bottom: none;
  border-left: 2px solid #ec3634;
}

.ola-tabs--right .ola-tabs-content {
  padding: 0 15px;
}

/* 位置：底部 */
.ola-tabs--bottom {
  flex-direction: column-reverse;
}

.ola-tabs--bottom .ola-tabs-header {
  border-bottom: none;
  border-top: 1px solid #e4e7ed;
}

.ola-tabs--bottom .ola-tabs-item.is-active {
  border-bottom: none;
  border-top: 2px solid #333;
}

/* 位置：左侧 */
.ola-tabs--left {
  flex-direction: row-reverse;
}

.ola-tabs--left .ola-tabs-header {
  flex-direction: column;
  border-bottom: none;
  border-right: 1px solid #e4e7ed;
}

.ola-tabs--left .ola-tabs-item.is-active {
  border-bottom: none;
  border-right: 2px solid #333;
}

.ola-tabs--left .ola-tabs-content {
  padding: 0 15px;
}
</style> 